import { useCallback, useEffect, useRef, useState } from "react";

// Cache module-level — persiste entre navigations, invalidé au refresh de page
const _dashCache = { progression: null, meanTime: null };
import { useAuth } from "../../hooks/use-auth";
import { gtm } from "../../lib/gtm";
import {
  Box,
  Container,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMounted } from "../../hooks/use-mounted";
import { topicsApi } from "../../api/topic-api";
import { auditsApi } from "../../api/audit-api";
import DashboardFormateur from "./dashbord-formateur";
import DashboardAdmin from "./dashboard-admin";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

const Dashboard = () => {
  const isMounted = useMounted();
  const [progression, setProgression] = useState(_dashCache.progression ?? []);
  const [meanTime,    setMeanTime]    = useState(_dashCache.meanTime    ?? []);
  const [isLoadingTime, setIsLoadingTime] = useState(!_dashCache.meanTime);
  const [isEditMode, setIsEditMode] = useState(false);
  const [permissions, setPermissions] = useState([]); // État pour stocker les permissions

  // État pour contrôler la taille de la page
  const [zoomLevel, setZoomLevel] = useState(0.7);
  const [width, setWidth] = useState("177%");

  const { user } = useAuth();
// Fonction pour vérifier si l'utilisateur a la permission "update" pour le composant "Dashboard"
  // Fonction pour récupérer les permissions (avec cache localStorage 5 min)
  const getPermissionsByRole = async (roles_id) => {
    const tenant = globalThis.localStorage.getItem('tenant');
    const cacheKey = `permissions_${tenant}_${roles_id}`;
    const cached = JSON.parse(globalThis.localStorage.getItem(cacheKey) || 'null');
    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
      setPermissions(cached.permissions);
      return;
    }
    const url = `${process.env.REACT_APP_BACK}/subdomain/${tenant}/api/permission-verification/get/`;
    const accessToken = globalThis.localStorage.getItem(`accessToken_${tenant}`);
    try {
      const resp = await fetch(url, {
        method: "POST",
        body: JSON.stringify({ roles_id }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!resp.ok) throw new Error(`HTTP error! Status: ${resp.status}`);
      const json = await resp.json();
      globalThis.localStorage.setItem(cacheKey, JSON.stringify({ permissions: json.permissions, timestamp: Date.now() }));
      setPermissions(json.permissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

    // const getMeanTime = useCallback(async () => {
    //   try {
    //     const data = await auditsApi.meanTime(user.admin_id);
    //     if (isMounted()) {
    //       setMeanTime(data);
    //       // console.log("largeur",window.innerWidth);
    //     }
    //   } catch (err) {
    //     console.error(err);
    //   } finally {
    //     setIsLoadingTime(false);
    //   }
    // });
      

  

  // Récupérer les permissions lorsque le composant est monté
  useEffect(() => {
    if (user && user.roles_id) {
      getPermissionsByRole(user.roles_id);
    }
  }, [user]);

  const getMeanTime = useCallback(async () => {
    try {
      const data = await auditsApi.meanTime(user.admin_id);
      if (isMounted()) {
        _dashCache.meanTime = data;
        setMeanTime(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingTime(false);
    }
  }, [isMounted]);

  const getProgression = useCallback(async () => {
    try {
      const data = await topicsApi.getProgression(user.admin_id);
      if (isMounted()) {
        _dashCache.progression = data;
        setProgression(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMounted]);

  // Gérer les changements de largeur de l'écran
  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;

      if (screenWidth >= 1300 && screenWidth <= 1370) {
        setWidth("176%");
        setZoomLevel(0.63);
      } else if (screenWidth >= 1390 && screenWidth <= 1440) {
        setWidth("167%");
        setZoomLevel(0.66);
      } else if (screenWidth >= 1500 && screenWidth < 1600) {
        setWidth("154%");
        setZoomLevel(0.69);
      } else if (screenWidth >= 1600 && screenWidth <= 1650) {
        setWidth("145%");
        setZoomLevel(0.74);
      } else if (screenWidth >= 1650 && screenWidth <= 1700) {
        setWidth("138%");
        setZoomLevel(0.78);
      } else if (screenWidth >= 1200 && screenWidth <= 1299) {
        setWidth("191%");
        setZoomLevel(0.585);
      } else if (screenWidth > 1700) {
        setWidth("120%");
        setZoomLevel(0.9);
      }
    };

    // Définir les valeurs initiales
    handleResize();

    // Ajouter un écouteur d'événements pour les redimensionnements
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    gtm.push({ event: "page_view" });
    // Si déjà en cache (retour depuis une autre page) → pas d'appel API
    if (_dashCache.progression && _dashCache.meanTime) return;
    Promise.all([getProgression(), getMeanTime()]);
  }, []);

  const handleToggleEditMode = () => {
    setIsEditMode((prev) => !prev);
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        pt: 2,
        pb: 2,
        width: width,
        backgroundColor: (theme) =>
          theme.palette.mode === "dark"
            ? "#2C2F3F"
            : isEditMode
            ? "white"
            : "#F9FAFB",
        transition: "background-color 0.3s ease",
        zoom: zoomLevel,
      }}
    >
      <Container maxWidth="xl" sx={{ padding: { xs: 2, sm: 3 } }}>
        {/* ── Header du tableau de bord ── */}
        <Box sx={{ mb: 3, ml: 6, mr: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                fontSize: { xs: "1.4rem", sm: "1.65rem" },
                color: "#2d3748",
                lineHeight: 1.2,
              }}
            >
              Tableau de Bord
            </Typography>
            <Tooltip
              title={isEditMode ? "Quitter le mode édition" : "Personnaliser le tableau de bord"}
              arrow
            >
              <Box
                onClick={handleToggleEditMode}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  backgroundColor: isEditMode ? "#6060B8" : "transparent",
                  color: isEditMode ? "#fff" : "#a0aec0",
                  transition: "all 0.22s ease",
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: isEditMode ? "#4848A0" : "rgba(96,96,184,0.1)",
                    color: isEditMode ? "#fff" : "#6060B8",
                  },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>
          </Box>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: "#a0aec0",
              mt: 0.4,
              fontWeight: 400,
              textTransform: "capitalize",
            }}
          >
            {new Date().toLocaleDateString("fr-FR", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Typography>
        </Box>

        {/* Séparateur visuel subtil */}
        <Box
          sx={{
            ml: 6,
            mr: 2,
            mb: 3,
            height: "2px",
            borderRadius: "2px",
            background: "linear-gradient(90deg, #6060B8 0%, rgba(96,96,184,0.08) 60%, transparent 100%)",
          }}
        />

        <DashboardAdmin
          progression={progression}
          admin_id={user.admin_id}
          isEditMode={isEditMode}
          id_service={user.id_service}
        />
      </Container>
    </Box>
  );
};

export default Dashboard;